// src/components/StudentManagement.tsx
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from './ui';
import { Search, Plus, Edit2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

interface Student {
  id: number;
  person_code: string;
  name: string;
  gender: string;
  dharma_name: string;
  birth_date: string;
  mobile_phone: string;
  email: string;
  current_status: string;
}

interface PaginatedResponse {
  items: Student[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

const StudentManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get page and search from URL parameters
  const currentPage = parseInt(searchParams.get('page') || '1');
  const searchTerm = searchParams.get('search') || '';
  
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage] = useState(10);

  // Update URL when search changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchParams(params => {
      if (newSearchTerm) {
        params.set('search', newSearchTerm);
      } else {
        params.delete('search');
      }
      params.set('page', '1'); // Reset to first page on new search
      return params;
    });
  };

  // Update URL when page changes
  const handlePageChange = (newPage: number) => {
    setSearchParams(params => {
      params.set('page', newPage.toString());
      return params;
    });
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        per_page: perPage.toString(),
        search: searchTerm
      });

      const response = await fetch(`/api/students?${queryParams}`);
      if (!response.ok) {
        throw new Error('Failed to fetch students');
      }

      const data: PaginatedResponse = await response.json();
      setStudents(data.items);
      setTotalPages(data.total_pages);
      setTotalItems(data.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [currentPage, searchTerm]); // Fetch when page or search changes

  const getStatusClass = (status: string) => {
    switch (status) {
      case '在林': return 'bg-green-100 text-green-800';
      case '退林': return 'bg-yellow-100 text-yellow-800';
      case '除名': return 'bg-red-100 text-red-800';
      case '失联': return 'bg-gray-100 text-gray-800';
      case '出家': return 'bg-purple-100 text-purple-800';
      case '往生': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-red-500 text-center">
              Error: {error}
              <Button
                onClick={fetchStudents}
                className="ml-2 bg-blue-500 hover:bg-blue-600"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>林员列表</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              共 {totalItems} 条记录
            </p>
          </div>
          <Link to="/students/new">
            <Button className="bg-blue-500 hover:bg-blue-600">
              <Plus className="w-4 h-4 mr-2" />
              新增林员
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="flex items-center mb-4">
            <Search className="w-4 h-4 mr-2 text-gray-500" />
            <Input
              placeholder="搜索林员..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="max-w-sm"
            />
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">编码</th>
                  <th className="p-2 text-left">姓名</th>
                  <th className="p-2 text-left">法名</th>
                  <th className="p-2 text-left">性别</th>
                  <th className="p-2 text-left">联系电话</th>
                  <th className="p-2 text-left">状态</th>
                  <th className="p-2 text-left">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">加载中...</td>
                  </tr>
                ) : students.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      {searchTerm ? '没有找到匹配的林员' : '暂无林员信息'}
                    </td>
                  </tr>
                ) : (
                  students.map((student) => (
                    <tr key={student.id} className="border-b hover:bg-gray-50">
                      <td className="p-2">{student.person_code}</td>
                      <td className="p-2">{student.name}</td>
                      <td className="p-2">{student.dharma_name}</td>
                      <td className="p-2">{student.gender}</td>
                      <td className="p-2">{student.mobile_phone}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded-full text-sm ${getStatusClass(student.current_status)}`}>
                          {student.current_status}
                        </span>
                      </td>
                      <td className="p-2">
                        <Link 
                          to={`/students/${student.id}/edit`}
                          state={{ from: { search: searchParams.toString() } }}
                        >
                          <Button variant="ghost" size="sm" className="hover:bg-gray-100">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-500">
                第 {currentPage} 页，共 {totalPages} 页
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                >
                  上一页
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                >
                  下一页
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentManagement;